import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { Flame, ArrowUpRight, ArrowUp, ArrowDown, Minus, MousePointerClick, MapPin, Users, Globe, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatHeat, labelForType, canonicalDomain, outboundUrl } from "@/lib/behot";
import { trackListingClick, type BoardRow, type Listing } from "@/lib/queries";
import { ShareRankButton } from "@/components/share-buttons";
import { useMyReferralCode } from "@/hooks/useReferral";
import { useT } from "@/i18n";
import { AmbassadorBadge, useEndorsedListingIds } from "@/components/ambassador-endorsements";
import { useServiceListingMap } from "@/hooks/useServiceListings";
import { Store } from "lucide-react";
import { proxiedImage } from "@/lib/remote-image";
import { mockBoardRows } from "@/lib/mock-board";


function Avatar({ listing }: { listing: Listing }) {
  const logo = proxiedImage(listing.logo_url);
  const [failed, setFailed] = useState(false);
  if (logo && !failed) {
    return (
      <img
        src={logo}
        alt=""
        width={48}
        height={48}
        decoding="async"
        onError={() => setFailed(true)}
        className="size-12 shrink-0 rounded-xl object-cover"
        loading="lazy"
      />

    );
  }
  return (
    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent text-lg font-bold text-accent-foreground">
      {listing.name.charAt(0).toUpperCase()}
    </div>
  );
}


export type BoardMovement = { isNew: boolean; previousRank: number | null; justMoved?: boolean };

/** Static pill: "New" for fresh entries, "Just moved" for a rank change in the last 24h. */
export function MovementBadge({ movement, className = "" }: { movement: BoardMovement; className?: string }) {
  const { t } = useT();
  if (!movement.isNew && !movement.justMoved) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        movement.isNew
          ? "bg-accent text-accent-foreground"
          : "bg-primary text-primary-foreground"
      } ${className}`}
    >
      <Sparkles className="size-3" /> {movement.isNew ? t("home.movers.new") : t("home.movers.badge")}
    </span>
  );
}

/** Music-chart style position change under the rank number: always visible. */
export function RankMovement({
  movement,
  rank,
}: {
  movement?: BoardMovement | undefined;
  rank: number;
}) {
  const { t } = useT();

  // No data yet → neutral unchanged.
  if (!movement) {
    return (
      <div className="mt-1 flex items-center justify-center gap-0.5 text-[11px] font-bold text-muted-foreground">
        <Minus className="size-3" />
      </div>
    );
  }

  // Brand-new entry on the chart.
  if (movement.isNew) {
    return (
      <div className="mt-1 flex items-center justify-center gap-0.5 text-[11px] font-bold uppercase text-primary">
        <Sparkles className="size-3" />
        {t("home.movers.new")}
      </div>
    );
  }

  // No previous rank known (but not flagged as new) → neutral unchanged.
  if (movement.previousRank === null) {
    return (
      <div className="mt-1 flex items-center justify-center gap-0.5 text-[11px] font-bold text-muted-foreground">
        <Minus className="size-3" />
      </div>
    );
  }

  // Unchanged position.
  if (movement.previousRank === rank) {
    return (
      <div className="mt-1 flex items-center justify-center gap-0.5 text-[11px] font-bold text-muted-foreground">
        <Minus className="size-3" />
      </div>
    );
  }

  // Moved up or down.
  const gained = movement.previousRank > rank;
  const delta = Math.abs(movement.previousRank - rank);
  return (
    <div
      className={`mt-1 flex items-center justify-center gap-0.5 text-[11px] font-bold ${
        gained ? "text-success" : "text-destructive"
      }`}
    >
      {gained ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
      {delta}
    </div>
  );
}


export function BoardEntry({
  row,
  rank,
  categorySlug,
  categoryLabel,
  movement,
  showTakePosition,
}: {
  row: BoardRow;
  rank: number;
  categorySlug?: string | undefined;
  categoryLabel?: { name: string; slug: string } | undefined;
  movement?: BoardMovement | undefined;
  showTakePosition?: boolean | undefined;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const { listing } = row;
  const boostSlug = categorySlug ?? categoryLabel?.slug;
  // Heat needed to overtake this position: current Heat + 1, with a 3 Heat floor.
  const currentHeat = Math.round(Number(row.active_heat_cents) / 433);
  const takeHeat = Math.max(3, currentHeat + 1);
  const refCode = useMyReferralCode();
  const endorsed = useEndorsedListingIds();
  const endorsement = endorsed[listing.id];
  const serviceMap = useServiceListingMap();
  const service = serviceMap[listing.id];
  const boardName = categoryLabel?.name ?? "BeHot.Now";
  const shareTitle = t("boards.entry.shareTitle", {
    name: listing.name,
    rank: String(rank).padStart(2, "0"),
    board: boardName,
    heat: formatHeat(row.active_heat_cents),
  });

  // The whole card acts like a banner ad: clicking anywhere opens the listing website.
  const websiteUrl = listing.website_url
    ? outboundUrl(listing.website_url, {
        slug: listing.slug,
        medium: "board",
        content: categoryLabel?.slug ?? categorySlug ?? "general",
        term: `rank-${String(rank).padStart(2, "0")}`,
      })
    : `/p/${listing.slug}`;
  const isExternal = Boolean(listing.website_url);

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const openListingTarget = () => {
    if (isExternal) {
      void trackListingClick(listing.slug);
      window.open(websiteUrl, "_blank", "noopener,noreferrer");
    } else {
      void navigate({ to: `/p/${listing.slug}` });
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={t("boards.entry.visitWebsite", { name: listing.name })}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("a,button,[role='menuitem'],input,select,textarea")) return;
        openListingTarget();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          if ((e.target as HTMLElement).closest("a,button")) return;
          e.preventDefault();
          openListingTarget();
        }
      }}
      className="card-surface group relative cursor-pointer p-3 transition-colors hover:border-primary/40 hover:shadow-sm sm:p-4"
    >
      <div className="relative z-10 flex items-start gap-3 sm:items-center sm:gap-4">

        <div className="w-8 shrink-0 text-center sm:w-14">
          <div className="rank-numeral text-xl leading-none sm:text-3xl">
            {String(rank).padStart(2, "0")}
          </div>
          <RankMovement movement={movement} rank={rank} />
        </div>
        <Avatar listing={listing} />
        <div className="min-w-0 flex-1">
          <span className="block truncate font-display text-base font-semibold group-hover:text-primary sm:text-lg">
            {listing.name}
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {movement && <MovementBadge movement={movement} />}
            {categoryLabel && (
              <Badge variant="secondary">
                {categoryLabel.name}
              </Badge>
            )}
            <Badge variant="outline">{labelForType(listing.listing_type)}</Badge>
            {endorsement && <AmbassadorBadge kind={endorsement} />}
            {service && (
              <Badge className="gap-1 bg-gradient-to-r from-primary to-primary/70 text-primary-foreground">
                <Store className="size-3" /> {t("profiles.marketplaceSeller")}
              </Badge>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:truncate">
            {listing.headline || listing.short_description || labelForType(listing.listing_type)}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-primary">
            <span className="flex items-center gap-1">
              <Flame className="size-3.5 shrink-0" /> {t("boards.entry.activeHeat", { amount: formatHeat(row.active_heat_cents) })}
            </span>
            {row.booster_count > 0 && (
              <span className="flex items-center gap-1 font-normal text-muted-foreground">
                <Users className="size-3.5 shrink-0" /> {t("boards.entry.boosterCount", { n: row.booster_count })}
              </span>
            )}
            {listing.website_url && (
              <span className="flex min-w-0 items-center gap-1 font-normal text-muted-foreground group-hover:text-primary">
                <Globe className="size-3.5 shrink-0" />
                <span className="truncate">{canonicalDomain(listing.website_url) ?? t("boards.entry.website")}</span>
              </span>
            )}

            <span className="flex items-center gap-1 font-normal text-muted-foreground">
              <MousePointerClick className="size-3.5 shrink-0" /> {t("boards.entry.clicks", { n: listing.click_count })}
            </span>
            {(listing.city || listing.country_code) && (
              <span className="flex items-center gap-1 font-normal text-muted-foreground">
                <MapPin className="size-3.5 shrink-0" />
                {[listing.city, listing.country_code].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        </div>
        <div className="hidden shrink-0 gap-2 sm:flex">
          <ShareRankButton
            path={`/p/${listing.slug}`}
            refCode={refCode}
            title={shareTitle}
          />
          <Button variant="outline" size="sm" onClick={(e) => { handleActionClick(e); navigate({ to: "/p/$slug", params: { slug: listing.slug } }); }}>
            {t("boards.entry.view")}
          </Button>
          <Button variant="hot" size="sm" onClick={(e) => { handleActionClick(e); navigate({ to: boostSlug ? "/boost" : "/get-hot", search: { listing: listing.slug, category: boostSlug } }); }}>
            {t("boards.entry.boost")} <ArrowUpRight className="size-3.5" />
          </Button>
        </div>
      </div>
      {listing.tags && listing.tags.length > 0 && (
        <div className="relative z-10 mt-3 flex flex-wrap gap-1.5 sm:pl-[4.5rem]">
          {listing.tags.slice(0, 5).map((tag) => (
            <Badge key={tag} variant="outline" className="text-[11px] font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      <div className="relative z-10 mt-3 grid grid-cols-[auto_1fr_1fr] items-center gap-2 sm:hidden">
        <ShareRankButton
          path={`/p/${listing.slug}`}
          refCode={refCode}
          title={shareTitle}
          label=""
        />
        <Button variant="outline" size="sm" className="w-full" onClick={(e) => { handleActionClick(e); navigate({ to: "/p/$slug", params: { slug: listing.slug } }); }}>
          {t("boards.entry.view")}
        </Button>
        <Button variant="hot" size="sm" className="w-full" onClick={(e) => { handleActionClick(e); navigate({ to: boostSlug ? "/boost" : "/get-hot", search: { listing: listing.slug, category: boostSlug } }); }}>
          {t("boards.entry.boost")}
        </Button>
      </div>
      {showTakePosition && boostSlug && (
        <div className="relative z-10 mt-3 sm:pl-[4.5rem]">
          <Button variant="outline" size="sm" className="w-full border-primary/50 text-primary sm:w-auto" onClick={(e) => { handleActionClick(e); navigate({ to: "/boost", search: { category: boostSlug, heat: takeHeat } }); }}>
            <Flame className="size-3.5" /> Take this position in {boardName} — {takeHeat} Heat
          </Button>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Heat added on the {boardName} board counts on the General board too.
          </p>
        </div>
      )}
    </div>
  );
}

export function DiscoverEntry({ listing }: { listing: Listing }) {
  return (
    <Link
      to="/p/$slug"
      params={{ slug: listing.slug }}
      className="card-surface flex items-center gap-3 p-3 transition-colors hover:border-primary/40"
    >
      <Avatar listing={listing} />
      <div className="min-w-0">
        <p className="truncate font-medium">{listing.name}</p>
        <p className="truncate text-sm text-muted-foreground">
          {listing.headline || labelForType(listing.listing_type)}
        </p>
      </div>
    </Link>
  );
}

/**
 * Placeholder rows shown only while a board has no real listings.
 * They are visually muted, clearly tagged "Mock listing" and disappear the
 * moment a real listing is placed on that board.
 */
export function MockBoardList({
  seedKey,
  categorySlug,
  count = 5,
}: {
  seedKey: string;
  categorySlug?: string | undefined;
  count?: number;
}) {
  const { t } = useT();
  const rows = mockBoardRows(seedKey, count);
  return (
    <div className="space-y-3 opacity-80" aria-hidden="true">
      {rows.map((row, i) => (
        <div
          key={row.id}
          className="card-surface relative overflow-hidden border-dashed p-3 sm:p-4"
        >
          <div className="flex items-start gap-3 sm:items-center sm:gap-4">
            <div className="w-8 shrink-0 text-center sm:w-14">
              <div className="rank-numeral text-xl leading-none text-muted-foreground sm:text-3xl">
                {String(i + 1).padStart(2, "0")}
              </div>
            </div>
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-muted text-lg font-bold text-muted-foreground">
              {row.name.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate font-display text-base font-semibold text-muted-foreground sm:text-lg">
                  {row.name}
                </span>
                <Badge variant="outline" className="border-dashed text-[10px] uppercase tracking-wider">
                  Mock listing
                </Badge>
              </div>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{row.headline}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Flame className="size-3.5 shrink-0" /> {formatHeat(row.heatCents)} Heat
                </span>
                <Badge variant="outline">{labelForType(row.type)}</Badge>
              </div>
            </div>
            <div className="hidden shrink-0 sm:block">
              <Button asChild variant="hot" size="sm">
                <Link to="/get-hot" search={{ category: categorySlug, listing: undefined }}>
                  <Flame className="size-3.5" /> {t("boards.empty.claimPosition")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      ))}
      <p className="text-center text-xs text-muted-foreground">
        Placeholder entries — they disappear as soon as a real listing takes this board.
      </p>
    </div>
  );
}

export function EmptyBoard({ categorySlug }: { categorySlug?: string | undefined }) {

  const { t } = useT();
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-background to-accent/20 p-6 text-center sm:p-10">
      <div className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary-foreground">
          <Flame className="size-3.5" /> {t("boards.empty.lastChance")}
        </span>

        <h3 className="mt-4 font-display text-2xl font-bold sm:text-3xl">
          {t("boards.empty.beThe")} <span className="text-gradient-hot">{t("boards.empty.first")}</span> {t("boards.empty.onThisBoard")}
        </h3>

        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
          {t("boards.empty.nobodyHolds")}{" "}
          <strong className="text-foreground">{t("boards.empty.justFive")}</strong> {t("boards.empty.cheapest")}
        </p>

        <div className="mx-auto mt-5 flex max-w-md flex-wrap items-center justify-center gap-2 text-xs font-semibold">
          <span className="rounded-full border border-primary/40 bg-background/70 px-3 py-1 text-primary">
            {t("boards.empty.entryNow")}
          </span>
          <span className="rounded-full border border-border bg-background/70 px-3 py-1 text-muted-foreground line-through">
            {t("boards.empty.afterFirstClaim")}
          </span>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button asChild variant="hot" size="lg" className="animate-pulse">
            <Link to="/get-hot" search={{ category: categorySlug, listing: undefined }}>
              <Flame className="size-4" /> {t("boards.empty.claimPosition")}
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/app/wallets">{t("boards.empty.loadHeatNow")}</Link>
          </Button>
        </div>

        <p className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground">
          {t("boards.empty.firstInFooter")}
        </p>
      </div>
    </div>
  );
}
